import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import ToothIcon from "@/components/ToothIcon";
import { ArrowRight, Building2, Smartphone, FileSpreadsheet, MessageCircle, ShieldCheck, Sparkles } from "lucide-react";

// Imagen estable de Unsplash. Estilo minimalista/profesional con espacio negativo.
// Para cambiar a otro estilo basta con sustituir el ID de Unsplash:
//  - photo-1606811971618-4486d14f3f99  herramientas dentales sobre fondo claro (default)
//  - photo-1588776814546-1ffcf47267a5  sonrisa close-up
//  - photo-1559757175-5700dde675bc     consultorio claro minimalista
//  - photo-1609840114035-3c981b782dfe  software dental en laptop
const HERO_IMAGE = "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1400&q=85";

const HIGHLIGHTS = [
  { icon: MessageCircle, title: "Recordatorios por WhatsApp",   sub: "Reduce hasta 30% el ausentismo de tus pacientes." },
  { icon: FileSpreadsheet, title: "Contabilidad y caja al día",  sub: "Cierre diario con cuadre por método de pago." },
  { icon: ShieldCheck,    title: "Historia clínica completa",    sub: "Odontograma, periodontograma y evoluciones firmadas." },
  { icon: Sparkles,       title: "White-label personalizable",   sub: "Logo, colores y plantillas con tu marca." },
];

export default function RootLogin() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { branding } = useBranding();

  // Slug guardado de la última sesión exitosa (auto-prefilled).
  const lastSlug = (typeof window !== "undefined" && localStorage.getItem("df_tenant")) || "";
  const [step, setStep] = useState(lastSlug ? "credentials" : "slug");
  const [slug, setSlug] = useState(lastSlug);
  const [tenant, setTenant] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);

  // Si ya hay sesión, redirige al app
  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  // Carrusel de bullets cada 4s
  useEffect(() => {
    const t = setInterval(() => setHighlightIdx((i) => (i + 1) % HIGHLIGHTS.length), 4000);
    return () => clearInterval(t);
  }, []);

  // Si tenemos slug guardado, intentar cargar nombre de tenant en background
  useEffect(() => {
    if (!slug) { setTenant(null); return; }
    api.get(`/tenants/by-slug/${slug}`)
      .then((r) => setTenant(r.data))
      .catch(() => setTenant(null));
  }, [slug]);

  const submitSlug = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const clean = slug.toLowerCase().trim().replace(/[^a-z0-9-]/g, "");
    if (!clean) { setError("Ingresa el dominio de tu clínica"); setLoading(false); return; }
    try {
      const { data } = await api.get(`/tenants/by-slug/${clean}`);
      setTenant(data);
      setSlug(clean);
      setStep("credentials");
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) setError("No encontramos una clínica con ese dominio. Verifícalo.");
      else if (status === 403) setError("Esta licencia está suspendida o vencida. Contacta soporte.");
      else setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo verificar el dominio");
    } finally { setLoading(false); }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const ok = await login(username, password, slug);
      if (ok) navigate("/", { replace: true });
      else setError("Credenciales inválidas");
    } catch {
      setError("Error iniciando sesión");
    } finally { setLoading(false); }
  };

  const Highlight = HIGHLIGHTS[highlightIdx];
  const HIcon = Highlight.icon;

  const clinicName = (step === "credentials" && tenant?.name) || branding.nombre_comercial || "DentCloud";

  return (
    <div className="min-h-screen w-full overflow-x-hidden grid grid-cols-1 lg:grid-cols-2 bg-background" data-testid="root-login">
      {/* IZQUIERDA · formulario */}
      <div className="flex items-center justify-center px-5 py-8 sm:p-10 lg:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 sm:mb-10">
            {branding.logo_principal_b64 ? (
              <img src={branding.logo_principal_b64} alt={branding.nombre_comercial} className="h-9 max-w-[180px] object-contain" />
            ) : (
              <>
                <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: "var(--brand-primary)", color: "#fff" }}>
                  <ToothIcon className="w-5 h-5" />
                </div>
                <span className="font-display font-bold tracking-tight text-xl">{branding.nombre_comercial || "DentCloud"}</span>
              </>
            )}
          </div>

          {step === "slug" && (
            <form onSubmit={submitSlug} className="space-y-5" data-testid="step-slug">
              <div>
                <div className="label-eyebrow mb-2 text-primary">Paso 1 de 2</div>
                <h1
                  className="font-display font-black tracking-tighter leading-[1.05] mb-3"
                  style={{ fontSize: "clamp(1.75rem, 6vw, 2.75rem)" }}
                >
                  Bienvenido a tu<br/>plataforma odontológica
                </h1>
                <p className="text-base sm:text-sm text-muted-foreground max-w-sm leading-relaxed">
                  Introduce el dominio de tu clínica para iniciar sesión.
                </p>
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Dominio de tu clínica</label>
                <div className="flex flex-col sm:flex-row sm:items-stretch border border-border rounded-sm overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30 transition">
                  <div className="flex items-center px-3 py-2 sm:py-0 bg-muted text-[11px] sm:text-xs font-mono text-muted-foreground border-b sm:border-b-0 sm:border-r border-border whitespace-nowrap">
                    https://dentcloud.online/
                  </div>
                  <input
                    type="text"
                    autoFocus
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder=""
                    className="flex-1 w-full px-3 py-3 min-h-[44px] bg-transparent outline-none font-mono text-base sm:text-sm min-w-0"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    data-testid="root-slug-input"
                  />
                </div>
                {error && <div className="mt-2 text-xs text-destructive" data-testid="root-error">{error}</div>}
              </div>

              <button type="submit" disabled={loading} className="btn-primary !h-12 min-h-[44px] w-full justify-center text-base" data-testid="root-slug-submit">
                {loading ? "Verificando…" : "Continuar"} <ArrowRight className="w-4 h-4" />
              </button>

              <div className="text-sm sm:text-xs text-muted-foreground space-y-2 pt-4 border-t border-border">
                <div>¿Eres administrador de la plataforma? <Link to="/superadmin" className="text-primary hover:underline">SuperAdmin</Link></div>
              </div>
            </form>
          )}

          {step === "credentials" && (
            <form onSubmit={submitLogin} className="space-y-5" data-testid="step-credentials">
              <div>
                <div className="label-eyebrow mb-2 text-primary">Paso 2 de 2</div>
                <h1
                  className="font-display font-black tracking-tighter leading-[1.05] mb-2"
                  style={{ fontSize: "clamp(1.75rem, 6vw, 2.75rem)" }}
                >
                  Bienvenido
                </h1>
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-bold uppercase tracking-wider truncate">{clinicName}</span>
                </div>
                <button type="button" onClick={() => { setStep("slug"); setError(""); }} className="text-xs text-muted-foreground hover:text-primary mt-1 underline min-h-[36px]" data-testid="root-change-slug">
                  No es mi clínica · cambiar dominio
                </button>
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Usuario</label>
                <input
                  type="text"
                  autoFocus
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="marta"
                  className="input-flat min-h-[44px] !h-12 sm:!h-11 text-base sm:text-sm"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="root-username"
                  required
                />
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Contraseña</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  className="input-flat min-h-[44px] !h-12 sm:!h-11 text-base sm:text-sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="root-password"
                  required
                />
              </div>

              {error && <div className="text-xs text-destructive" data-testid="root-login-error">{error}</div>}

              <button type="submit" disabled={loading} className="btn-primary !h-12 min-h-[44px] w-full justify-center text-base" data-testid="root-login-submit">
                {loading ? "Ingresando…" : "Ingresar"} <ArrowRight className="w-4 h-4" />
              </button>

              <div className="text-sm sm:text-xs text-muted-foreground pt-4 border-t border-border leading-relaxed">
                ¿Olvidaste tu contraseña? Pide a tu administrador que la restablezca desde Ajustes → Usuarios.
              </div>
            </form>
          )}
        </div>
      </div>

      {/* DERECHA · hero */}
      <div className="relative order-1 lg:order-2 min-h-[280px] sm:min-h-[360px] lg:min-h-screen overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Consultorio odontológico moderno"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        {/* Overlay oscuro base para legibilidad */}
        <div className="absolute inset-0 bg-black/55" />
        {/* Overlay con color del branding */}
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, rgba(var(--brand-primary-rgb), 0.85) 0%, rgba(0,0,0,0.5) 100%)`,
        }} />

        <div className="relative z-10 h-full flex flex-col justify-between gap-6 p-6 sm:p-10 lg:p-12 text-white">
          <div className="flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-widest font-bold opacity-90">
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">Plataforma odontológica</span>
          </div>

          <div className="space-y-5 sm:space-y-6 max-w-md">
            <h2
              className="font-display font-black tracking-tighter leading-[1.0]"
              style={{ fontSize: "clamp(1.75rem, 5.5vw, 3rem)" }}
            >
              Toda tu clínica<br/>en una plataforma
            </h2>
            <p className="text-base sm:text-base opacity-90 leading-relaxed">
              Gestiona pacientes, agenda, caja, contabilidad e historia clínica desde un solo lugar.
              Ya con confianza por consultorios en Colombia.
            </p>

            {/* Carrusel rotativo */}
            <div className="bg-black/30 backdrop-blur-md rounded-sm p-4 sm:p-5 border border-white/15 transition-all duration-500" key={highlightIdx}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-white/15 flex items-center justify-center shrink-0">
                  <HIcon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-base mb-0.5 break-words">{Highlight.title}</div>
                  <div className="text-sm opacity-90 leading-snug">{Highlight.sub}</div>
                </div>
              </div>
              <div className="flex gap-1.5 mt-4">
                {HIGHLIGHTS.map((_, i) => (
                  <button key={i} type="button" onClick={() => setHighlightIdx(i)}
                    className={`h-2 rounded-full transition-all ${i === highlightIdx ? "bg-white w-7" : "bg-white/40 w-3"}`}
                    aria-label={`Slide ${i + 1}`} />
                ))}
              </div>
            </div>
          </div>

          {/* Footer del hero — apilado en móvil, en línea en desktop */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[11px] uppercase tracking-widest font-bold opacity-80">
            <div>{branding.nombre_comercial || "DentCloud"}</div>
            <div>Colombia</div>
          </div>
        </div>
      </div>
    </div>
  );
}
